import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApi } from "@/context/ApiContext";
import { isSigningOut } from "@/api/session";
import { safeNextPath } from "@/lib/utils";

/**
 * Signing in happens on the SSO, so a signed-out visitor is sent straight
 * there. This page only shows when that can't happen: the session failed to
 * restore, or the redirect itself failed (no Web Crypto on plain HTTP).
 */
export default function Login() {
  const api = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  const next = safeNextPath(new URLSearchParams(location.search).get("next"));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const redirected = useRef(false);
  const problem = error || api.sessionError;

  async function login() {
    setLoading(true);
    setError("");
    try {
      await api.login(next);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Sign in failed");
      setLoading(false);
    }
  }

  useEffect(() => {
    if (api.authStatus === "authenticated") {
      navigate(next, { replace: true });
      return;
    }
    // Once, and only when we know there is no session (not while it restores,
    // nor while signing out: that navigation is already on its way to the SSO).
    if (api.authStatus === "guest" && !api.sessionError && !redirected.current && !isSigningOut()) {
      redirected.current = true;
      void login();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.authStatus, api.sessionError, next, navigate]);

  if (!problem) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg-app)] text-sm text-[var(--text-muted)]">
        Redirecting to sign in…
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bg-app)] px-4 py-10">
      <div className="flex w-full max-w-[440px] flex-col gap-7">
        <div className="flex items-center justify-center gap-2.5">
          <img src="/tradestorm-logo.svg" alt="" className="h-10 w-10" />
          <span className="text-xl font-semibold">TradeStorm</span>
        </div>
        <div className="card flex flex-col gap-5 p-7 sm:p-8">
          <h1 className="m-0 text-[26px] font-semibold">Sign in</h1>
          <div role="alert" className="text-sm text-[var(--danger)]">
            {problem}
          </div>
          <button className="btn btn-primary h-12" disabled={loading} onClick={() => void login()}>
            {loading ? "Redirecting…" : "Try again"}
          </button>
        </div>
      </div>
    </div>
  );
}
